import { ref, reactive, Ref } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables/index';
import { useI18n } from 'vue-i18n';

type MessagePayload = Record<string, unknown>;

interface MessageSchedulerContext {
  mode: 'new' | 'reschedule';
  messageId: number | null;
  conversationId: number | null;
  messagePayload: MessagePayload | null;
}

const ensureScheduledAtDateIsIso = (selectedDate: string | Date | null) => {
  let isoDateString = '';
  if (selectedDate instanceof Date) {
    isoDateString = selectedDate.toISOString();
  } else if (selectedDate) {
    isoDateString = new Date(selectedDate).toISOString();
  }
  return isoDateString;
};

export function useMessageScheduler() {
  const store = useStore();
  const { t } = useI18n();

  const isDateTimePickerVisible = ref(false);
  const scheduledDate = ref<string | Date | null>(null);
  const onSuccessCallback = ref<(() => void) | null>(null);

  const context = reactive<MessageSchedulerContext>({
    mode: 'new',
    messageId: null,
    conversationId: null,
    messagePayload: null,
  });

  const openForSchedule = (
    newMessagePayload: MessagePayload,
    onConfirmScheduleSuccessCallback?: () => void
  ) => {
    context.mode = 'new';
    context.messagePayload = newMessagePayload;
    scheduledDate.value = new Date(Date.now() + 60 * 60 * 1000);
    isDateTimePickerVisible.value = true;
    if (onConfirmScheduleSuccessCallback) {
      onSuccessCallback.value = onConfirmScheduleSuccessCallback;
    }
  };

  const openForReschedule = (
    conversationId: number,
    existingMessageId: number,
    currentDate: string | Date | null,
    onConfirmScheduleSuccessCallback?: () => void
  ) => {
    context.mode = 'reschedule';
    context.conversationId = conversationId;
    context.messageId = existingMessageId;
    scheduledDate.value = currentDate || null;
    isDateTimePickerVisible.value = true;
    if (onConfirmScheduleSuccessCallback) {
      onSuccessCallback.value = onConfirmScheduleSuccessCallback;
    }
  };

  const close = () => {
    isDateTimePickerVisible.value = false;
    scheduledDate.value = null;
    context.messagePayload = null;
    context.messageId = null;
    context.conversationId = null;
    onSuccessCallback.value = null;
  };

  const dispatchScheduleNewMessage = async () => {
    await store.dispatch('createPendingMessageAndSend', {
      ...context.messagePayload,
      scheduledAt: ensureScheduledAtDateIsIso(scheduledDate.value),
    });
  };

  const dispatchRescheduleMessage = async () => {
    await store.dispatch('rescheduleMessage', {
      conversationId: context.conversationId,
      messageId: context.messageId,
      scheduledAt: ensureScheduledAtDateIsIso(scheduledDate.value),
    });
  };

  const confirmSchedule = async () => {
    if (!scheduledDate.value) return;

    const timeToSchedule = new Date(scheduledDate.value).getTime();
    const oneMinuteAgo = Date.now() - 60 * 1000;

    if (timeToSchedule < oneMinuteAgo) {
      useAlert(t('MESSAGES.SCHEDULE.PAST_DATE_ERROR') || t('GENERAL.ERROR'));
      return;
    }

    try {
      if (context.mode === 'new') {
        await dispatchScheduleNewMessage();
      } else if (context.mode === 'reschedule') {
        await dispatchRescheduleMessage();
      }

      if (onSuccessCallback.value) {
        onSuccessCallback.value();
        onSuccessCallback.value = null;
      }
      close();
    } catch {
      useAlert(t('GENERAL.ERROR'));
    }
  };

  const sendNow = async (conversationId: number, existingMessageId: number) => {
    context.mode = 'reschedule';
    context.conversationId = conversationId;
    context.messageId = existingMessageId;
    scheduledDate.value = new Date();
    await confirmSchedule();
  };

  return {
    isDateTimePickerVisible,
    scheduledDate,
    openForSchedule,
    openForReschedule,
    close,
    confirmSchedule,
    sendNow,
  };
}
